import { InlineIcon } from '@iconify/react';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { MutableRefObject, ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'src/components/design-system/Button';
import Checkbox from 'src/components/design-system/Checkbox';
import Dialog from 'src/components/design-system/Dialog';
import useLocalStorage from 'src/hooks/useLocalstorage';
import useStep from 'src/hooks/useStep';
import useAutoFocus from './useAutoFocus';

interface Step {
  content: ReactNode;
  title: string | React.ReactElement<any>;
}

interface GuideMeProps {
  steps: Step[];
  guideId: string;
  focusRef?: MutableRefObject<any>;
}

const useGuideMe = ({ steps, guideId, focusRef }: GuideMeProps) => {
  const [
    step,
    { canGoToNextStep, canGoToPrevStep, goToNextStep, goToPrevStep, reset, isLastStep },
  ] = useStep(steps.length);
  const [dontShowOnLoad, setDontShowOnLoad] = useLocalStorage(guideId, false);
  const [open, setOpen] = useState(false);
  const buttonRef = useAutoFocus(open, [
    { canGoToNextStep, canGoToPrevStep, goToNextStep, goToPrevStep, reset, isLastStep },
  ]);

  const handleOpenGuideMeIfDontShowNotEnabled = useCallback(() => {
    setTimeout(() => {
      setDontShowOnLoad((prev) => {
        setOpen(!prev);
        return prev;
      });
    }, 100);
  }, [setDontShowOnLoad]);

  const handleOpenGuideMe = useCallback(() => {
    setOpen(true);
  }, []);

  const handleCloseGuideMe = useCallback(() => {
    setOpen(false);
    reset();
    if (focusRef?.current) {
      buttonRef.current?.blur();
      focusRef?.current.focus();
    }
  }, [buttonRef, focusRef, reset]);

  const currentStep = useMemo(() => steps[step - 1], [step, steps]);

  const { t } = useTranslation();
  const guideMe = useMemo(
    () => (
      <>
        <Button
          onClick={handleOpenGuideMe}
          endIcon={<InlineIcon icon="material-symbols:play-circle" />}
          color="secondary"
        >
          {t('Guide Me')}
        </Button>
        <Dialog
          onClose={handleCloseGuideMe}
          open={open && !!steps.length}
          width={500}
          title={currentStep?.title}
        >
          <div className="px-2 max-h-[65vh]">
            {currentStep?.content}
            {isLastStep && (
              <div className={`flex items-center gap-1 mt-2 font-semibold text-[13px]`}>
                <div>{t('Click the')}</div>
                <Button
                  endIcon={<InlineIcon icon="material-symbols:play-circle" />}
                  color="secondary"
                  readonly={true}
                >
                  {t('Guide Me')}
                </Button>
                <div className="font-semibold"> {t('button to show this again.')}</div>
              </div>
            )}
          </div>
          <DialogActionsBar>
            <div className="flex items-center justify-between">
              <Checkbox
                label="Please don't show this again"
                checked={dontShowOnLoad}
                onChange={setDontShowOnLoad}
              />
              <div className="flex items-center gap-1">
                {canGoToPrevStep && (
                  <Button color="info" onClick={goToPrevStep} onKeyDown={goToPrevStep}>
                    {t('Back')}
                  </Button>
                )}
                {canGoToNextStep && (
                  <Button
                    color="info"
                    onClick={goToNextStep}
                    onKeyDown={goToNextStep}
                    ref={buttonRef}
                  >
                    {t('Next')}
                  </Button>
                )}
                {isLastStep && (
                  <Button
                    ref={buttonRef}
                    color="info"
                    onClick={handleCloseGuideMe}
                    onKeyDown={handleCloseGuideMe}
                  >
                    {t('Close')}
                  </Button>
                )}
              </div>
            </div>
          </DialogActionsBar>
        </Dialog>
      </>
    ),
    [
      buttonRef,
      canGoToNextStep,
      canGoToPrevStep,
      currentStep?.content,
      currentStep?.title,
      dontShowOnLoad,
      goToNextStep,
      goToPrevStep,
      handleCloseGuideMe,
      handleOpenGuideMe,
      isLastStep,
      open,
      setDontShowOnLoad,
      steps.length,
      t,
    ]
  );

  return {
    guideMe,
    openGuideMe: handleOpenGuideMeIfDontShowNotEnabled,
  };
};

export default useGuideMe;
